import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getRequestState,
  getQuoteCount,
  isDeadWithNoQuotes,
  formatShortDate,
  REQUEST_STATE_COLORS,
} from '../utils/requestState';

const ADMIN_USER_ID = 110;
const API = 'https://quotebid-backend-production.up.railway.app';

interface User {
  id: number;
  name: string;
  email: string;
  user_type: string;
  subscription_tier: string;
  created_at: string;
  email_verified: boolean;
  business_name?: string;
  is_trial?: boolean;
  trial_plan?: string | null;
  trial_ends_at?: string | null;
}

interface QuoteRequest {
  id: number;
  title: string;
  category: string;
  location: string;
  budget_min: number;
  budget_max: number;
  user_id: number;
  // REQUEST-EXPIRY-VISIBILITY1 Phase A additions. quote_count is a STRING —
  // pg maps bigint to string with no ::int cast on the backend.
  status?: string;
  created_at?: string;
  expires_at?: string | null;
  city?: string | null;
  state?: string | null;
  quote_count?: string;
}

interface Business {
  id: number;
  business_name: string;
  email: string;
}

interface Stats {
  totalUsers: number;
  totalBusinesses: number;
  totalCustomers: number;
  totalRequests: number;
  totalQuotes: number;
  newToday: number;
  newThisWeek: number;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'business' | 'customer'>('all');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);

  // Push request state
  const [showPushModal, setShowPushModal] = useState(false);
  const [requests, setRequests] = useState<QuoteRequest[]>([]);
  // Push modal keeps its own list. It is fed by /quotes/requests, which filters
  // status='open' AND expires_at > NOW() — i.e. only requests a business could
  // actually still bid on. The table below now shows every request, so the two
  // lists can no longer share one state slot without the modal clobbering it.
  const [pushRequests, setPushRequests] = useState<QuoteRequest[]>([]);
  const businesses = users.filter(u => u.user_type === 'business');
  const [selectedRequest, setSelectedRequest] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [trialUser, setTrialUser] = useState<User | null>(null);
  const [trialTier, setTrialTier] = useState('pro');
  const [trialDays, setTrialDays] = useState('14');
  const [trialMsg, setTrialMsg] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState<number | null>(null);

  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;
  // Advisory client gate. Reads the real is_admin flag, keeping id===110 as a
  // fallback (matches backend isAdminUser = is_admin || id===110). Backend JWT
  // enforcement remains the real security.
  const isAdmin = !!user && (user.is_admin === true || user.id === ADMIN_USER_ID);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Unauthorized');
      const data = await res.json();
      setUsers(data.users || []);
      setStats(data.stats || null);
      // The request table used to load from /quotes/requests, which filters
      // status='open' AND expires_at > NOW(). That is precisely why admin could
      // never see an expired or closed request — they were dropped server-side
      // before they reached the page. /admin/dashboard returns every request
      // with status, expires_at, city/state and quote_count, so read it here.
      setRequests(data.requests || []);
    } catch (e) {
      setError('Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPushData = async () => {
    try {
      const res = await fetch(`${API}/quotes/requests`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setPushRequests(data.requests || []);
    } catch (e) {
      console.error("Failed to load requests", e);
    }
  };

  const handleOpenPushModal = () => {
    setShowPushModal(true);
    setPushMessage('');
    setSelectedRequest('');
    setSelectedBusiness('');
    fetchPushData();
  };

  const handlePushRequest = async () => {
    if (!selectedRequest || !selectedBusiness) {
      setPushMessage('Please select both a request and a business.');
      return;
    }
    setPushing(true);
    setPushMessage('');
    try {
      const res = await fetch(`${API}/admin/push-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ request_id: parseInt(selectedRequest), business_id: parseInt(selectedBusiness) })
      });
      const data = await res.json();
      if (res.ok) {
        setPushMessage('Success: ' + data.message);
      } else {
        setPushMessage('Error: ' + data.error);
      }
    } catch (e) {
      setPushMessage('Failed to push request.');
    } finally {
      setPushing(false);
    }
  };

  const handleTrialUpgrade = async () => {
    if (!trialUser) return;
    setTrialLoading(true); setTrialMsg('');
    try {
      const res = await fetch(API + '/admin/trial-upgrade', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }, body: JSON.stringify({ user_id: trialUser.id, tier: trialTier, days: parseInt(trialDays) }) });
      const data = await res.json();
      if (res.ok) {
        const rawEnds = data.trial_ends_at;
        const d = rawEnds ? new Date(rawEnds) : null;
        const expiryStr = (d && !isNaN(d.getTime()))
          ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          : null;

        if (expiryStr) {
          setTrialMsg('Success! ' + trialUser.name + ' upgraded to ' + trialTier.toUpperCase() + ' — trial expires ' + expiryStr + '.');
        } else {
          // Fallback if response is malformed — don't show "Invalid Date"
          setTrialMsg('Success! ' + trialUser.name + ' upgraded to ' + trialTier.toUpperCase() + '.');
        }

        setUsers(prev => prev.map(u => u.id === trialUser!.id ? { ...u, subscription_tier: trialTier, is_trial: true, trial_plan: trialTier, trial_ends_at: data.trial_ends_at } : u));
      }
      else { setTrialMsg('Error: ' + data.error); }
    } catch (e) { setTrialMsg('Failed to upgrade user.'); }
    finally { setTrialLoading(false); }
  };



  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await fetch(`${API}/admin/admin-users/${confirmDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(prev => prev.filter(u => u.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (e) {
      setError('Failed to delete user.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteRequest = async (requestId: number) => {
    if (!window.confirm('Delete this request? This cannot be undone.')) return;
    setDeletingRequestId(requestId);
    try {
      const res = await fetch(API + '/admin/requests/' + requestId, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (res.ok) { setRequests(prev => prev.filter(r => r.id !== requestId)); }
      else { alert('Error: ' + data.error); }
    } catch (e) { alert('Failed to delete request.'); }
    finally { setDeletingRequestId(null); }
  };

  const filteredUsers = users
    .filter(u => filter === 'all' || u.user_type === filter)
    .filter(u =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.business_name?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const tierBadge = (tier: string) => {
    const colors: Record<string, { bg: string; color: string }> = {
      premium: { bg: '#0f172a', color: '#fff' },
      pro: { bg: '#3b82f6', color: '#fff' },
      starter: { bg: '#f1f5f9', color: '#64748b' },
    };
    const c = colors[tier?.toLowerCase()] || colors.starter;
    return (
      <span style={{ background: c.bg, color: c.color, padding: '2px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const }}>
        {tier || 'Starter'}
      </span>
    );
  };

  const trialBadge = (u: User) => {
    if (!u.is_trial || !u.trial_ends_at) return null;

    const d = new Date(u.trial_ends_at);
    if (isNaN(d.getTime())) return null;  // guard: bail on invalid dates

    const short = d.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
    });

    return (
      <span style={{
        padding: '2px 10px',
        borderRadius: '99px',
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        backgroundColor: '#fffbeb',
        color: '#b45309',
      }}>
        TRIAL · exp {short}
      </span>
    );
  };

  // REQUEST-EXPIRY-VISIBILITY1: Active / Expired / Closed, derived client-side.
  const requestStateBadge = (r: QuoteRequest) => {
    const state = getRequestState(r.status, r.expires_at);
    const c = REQUEST_STATE_COLORS[state];
    return (
      <span style={{
        background: c.bg,
        color: c.color,
        border: '1px solid ' + c.border,
        padding: '2px 10px',
        borderRadius: '99px',
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        whiteSpace: 'nowrap' as const,
      }}>
        {state}
      </span>
    );
  };

  // city/state is the populated pair on recent rows; older rows only have the
  // free-text location column. Render whichever exists rather than location
  // alone, which is null on most rows and was showing "-" for all of them.
  const requestLocation = (r: QuoteRequest) => {
    const parts = [r.city, r.state].filter(p => !!p && String(p).trim() !== '');
    if (parts.length > 0) return parts.join(', ');
    if (r.location && r.location.trim() !== '') return r.location;
    return '-';
  };

  const deadNoQuoteCount = requests.filter(
    r => isDeadWithNoQuotes(getRequestState(r.status, r.expires_at), r.quote_count)
  ).length;

  if (!isAdmin) return null;

  return (
    <div style={styles.page}>

      {/* Delete Confirm Modal */}
      {trialUser && (<div style={styles.modalOverlay}><div style={styles.modal}><h3 style={styles.modalTitle}>Trial Upgrade</h3><p style={styles.modalText}>Give <strong>{trialUser.name}</strong> ({trialUser.business_name || trialUser.email}) a free trial upgrade.</p><div style={{ marginBottom: 16 }}><label style={styles.label}>Upgrade To</label><select style={styles.select} value={trialTier} onChange={e => setTrialTier(e.target.value)}><option value="pro">Pro (/mo value)</option><option value="premium">Premium (/mo value)</option></select></div><div style={{ marginBottom: 16 }}><label style={styles.label}>Trial Duration</label><select style={styles.select} value={trialDays} onChange={e => setTrialDays(e.target.value)}><option value="7">7 days</option><option value="14">14 days (recommended)</option><option value="30">30 days</option></select></div><div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}><p style={{ margin: 0, fontSize: 13, color: '#166534', fontWeight: 600 }}>Gives {trialUser.name} {trialDays} days of {trialTier.toUpperCase()} access. No charge.</p></div>{trialMsg && <p style={{ fontSize: 14, color: trialMsg.startsWith('Success') ? '#10b981' : '#dc2626', marginBottom: 16, fontWeight: 600 }}>{trialMsg}</p>}<div style={styles.modalButtons}><button style={styles.cancelBtn} onClick={() => { setTrialUser(null); setTrialMsg(''); }}>Close</button><button style={{ ...styles.confirmDeleteBtn, background: 'linear-gradient(135deg, #3b82f6, #10b981)' }} onClick={handleTrialUpgrade} disabled={trialLoading}>{trialLoading ? 'Upgrading...' : 'Activate Trial'}</button></div></div></div>)}
      {confirmDelete && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Delete User</h3>
            <p style={styles.modalText}>
              Permanently delete <strong>{confirmDelete.name}</strong> ({confirmDelete.email})? This cannot be undone.
            </p>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button style={styles.confirmDeleteBtn} onClick={handleDeleteConfirm}>
                {deletingId ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push Request Modal */}
      {showPushModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Push Request to Business</h3>
            <p style={styles.modalText}>Manually send a customer request notification to a specific business.</p>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Select Request</label>
              <select style={styles.select} value={selectedRequest} onChange={e => setSelectedRequest(e.target.value)}>
                <option value="">-- Choose a Request --</option>
                {pushRequests.map((r: any) => (
                  <option key={r.id} value={r.id}>#{r.id} - {r.title} ({r.category})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Select Business</label>
              <select style={styles.select} value={selectedBusiness} onChange={e => setSelectedBusiness(e.target.value)}>
                <option value="">-- Choose a Business --</option>
                {businesses.map((b: any) => (
                  <option key={b.id} value={b.id}>{b.business_name} ({b.email})</option>
                ))}
              </select>
            </div>

            {pushMessage && (
              <p style={{ fontSize: 14, color: pushMessage.startsWith('Success') ? '#10b981' : '#dc2626', marginBottom: 16 }}>
                {pushMessage}
              </p>
            )}

            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={() => setShowPushModal(false)}>Close</button>
              <button
                style={{ ...styles.confirmDeleteBtn, background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}
                onClick={handlePushRequest}
                disabled={pushing}
              >
                {pushing ? 'Sending...' : 'Push Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Admin Dashboard</h1>
          <p style={styles.subtitle}>QuoteBid Platform Overview</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          <button style={styles.navBtn} onClick={() => navigate('/admin/prospects')}>Prospect Tool</button>
          <button style={styles.navBtn} onClick={() => navigate('/admin/social')}>Social Media</button>
          <button style={styles.navBtn} onClick={() => navigate('/admin/disputes')}>Disputes</button>
          <button style={{ ...styles.navBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #93c5fd' }} onClick={handleOpenPushModal}>Push Request</button>
          <button style={styles.refreshBtn} onClick={fetchData}>Refresh</button>
        </div>
      </div>

      {loading && <div style={styles.loading}>Loading...</div>}
      {error && <div style={styles.error}>{error}</div>}

      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}><div style={styles.statNumber}>{stats.totalUsers}</div><div style={styles.statLabel}>Total Users</div></div>
          <div style={styles.statCard}><div style={{ ...styles.statNumber, color: '#3b82f6' }}>{stats.totalBusinesses}</div><div style={styles.statLabel}>Businesses</div></div>
          <div style={styles.statCard}><div style={{ ...styles.statNumber, color: '#10b981' }}>{stats.totalCustomers}</div><div style={styles.statLabel}>Customers</div></div>
          <div style={styles.statCard}><div style={{ ...styles.statNumber, color: '#f59e0b' }}>{stats.totalRequests}</div><div style={styles.statLabel}>Total Requests</div></div>
          <div style={styles.statCard}><div style={{ ...styles.statNumber, color: '#8b5cf6' }}>{stats.totalQuotes}</div><div style={styles.statLabel}>Total Quotes</div></div>
          <div style={styles.statCard}><div style={{ ...styles.statNumber, color: '#ef4444' }}>{stats.newToday}</div><div style={styles.statLabel}>New Today</div></div>
          <div style={styles.statCard}><div style={{ ...styles.statNumber, color: '#06b6d4' }}>{stats.newThisWeek}</div><div style={styles.statLabel}>New This Week</div></div>
        </div>
      )}

      {!loading && !error && (
        <div style={styles.tableSection}>
          <div style={styles.tableHeader}>
            <div style={styles.filterGroup}>
              {(['all', 'business', 'customer'] as const).map(f => (
                <button
                  key={f}
                  style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? `All (${users.length})` : f === 'business' ? `Businesses (${users.filter(u => u.user_type === 'business').length})` : `Customers (${users.filter(u => u.user_type === 'customer').length})`}
                </button>
              ))}
            </div>
            <input
              style={styles.search}
              placeholder="Search by name, email, or business..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Business Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Plan</th>
                  <th style={styles.th}>Verified</th>
                  <th style={styles.th}>Joined</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, i) => (
                  <tr key={u.id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{u.id}</td>
                    <td style={styles.td}><strong>{u.name}</strong></td>
                    <td style={styles.td}>{u.email}</td>
                    <td style={styles.td}>{u.business_name || '-'}</td>
                    <td style={styles.td}>
                      <span style={{ background: u.user_type === 'business' ? '#eff6ff' : '#f0fdf4', color: u.user_type === 'business' ? '#2563eb' : '#16a34a', padding: '2px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const }}>
                        {u.user_type}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        {tierBadge(u.subscription_tier)}
                        {trialBadge(u)}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: u.email_verified ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                        {u.email_verified ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={styles.td}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      {u.user_type === 'business' && (<button style={styles.trialBtn} onClick={() => { setTrialUser(u); setTrialMsg(''); setTrialTier('pro'); setTrialDays('14'); }}>Trial</button>)}
                      {u.id !== ADMIN_USER_ID && (
                        <button style={styles.deleteBtn} onClick={() => setConfirmDelete(u)} disabled={deletingId === u.id}>
                          {deletingId === u.id ? '...' : 'Delete'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={styles.hint}>{filteredUsers.length} users shown</p>
        </div>
      )}
      {requests.length > 0 && (
        <div style={{ ...styles.tableSection, marginTop: 24 }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>
              Quote Requests ({requests.length})
            </div>
            {deadNoQuoteCount > 0 && (
              <div style={styles.deadBanner}>
                {deadNoQuoteCount} request{deadNoQuoteCount === 1 ? '' : 's'} ended with zero quotes — the homeowner got nothing and was never told.
              </div>
            )}
          </div>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead><tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Location</th>
                <th style={styles.th}>Budget</th>
                <th style={styles.th}>Quotes</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Expires</th>
                <th style={styles.th}>Action</th>
              </tr></thead>
              <tbody>
                {requests.map((r, i) => {
                  const state = getRequestState(r.status, r.expires_at);
                  const quotes = getQuoteCount(r.quote_count);
                  const dead = isDeadWithNoQuotes(state, r.quote_count);
                  return (
                  <tr key={r.id} style={dead ? styles.trDead : (i % 2 === 0 ? styles.trEven : styles.trOdd)}>
                    <td style={styles.td}>{r.id}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {requestStateBadge(r)}
                        {dead && <span style={styles.deadBadge}>0 quotes</span>}
                      </div>
                    </td>
                    <td style={styles.td}><strong>{r.title}</strong></td>
                    <td style={styles.td}>{r.category}</td>
                    <td style={styles.td}>{requestLocation(r)}</td>
                    <td style={styles.td}>{r.budget_min && r.budget_max ? '$' + r.budget_min + ' - $' + r.budget_max : 'Not specified'}</td>
                    <td style={{ ...styles.td, fontWeight: 700, color: quotes === 0 ? '#dc2626' : '#0f172a' }}>{quotes}</td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' as const }}>{formatShortDate(r.created_at)}</td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' as const }}>{formatShortDate(r.expires_at)}</td>
                    <td style={styles.td}>
                      <button style={styles.deleteBtn} onClick={() => { if(window.confirm('Delete this request?')) { fetch(API + '/admin/requests/' + r.id, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } }).then(() => setRequests(prev => prev.filter(x => x.id !== r.id))); } }}>Delete</button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1200, margin: '0 auto', padding: '32px 24px', fontFamily: "'DM Sans', system-ui, sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' },
  subtitle: { color: '#64748b', margin: '4px 0 0', fontSize: 14 },
  navBtn: { padding: '8px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  refreshBtn: { padding: '8px 16px', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  loading: { textAlign: 'center', padding: 40, color: '#64748b', fontSize: 16 },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: 10, marginBottom: 20 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 16, marginBottom: 32 },
  statCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '20px 16px', textAlign: 'center' as const, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  statNumber: { fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1, marginBottom: 8 },
  statLabel: { fontSize: 11, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600 },
  tableSection: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', gap: 16, flexWrap: 'wrap' as const },
  filterGroup: { display: 'flex', gap: 8 },
  filterBtn: { padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  filterBtnActive: { background: '#0f172a', color: '#fff', border: '1px solid #0f172a', fontWeight: 700 },
  search: { padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, width: 280, outline: 'none' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { padding: '12px 16px', textAlign: 'left' as const, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' as const },
  td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' as const },
  trEven: { background: '#fff' },
  trOdd: { background: '#fafafa' },
  // Zero-quote dead requests: the point of REQUEST-EXPIRY-VISIBILITY1. Left
  // rule plus a red wash so they read at a glance against the striped rows.
  trDead: { background: '#fef2f2', boxShadow: 'inset 4px 0 0 0 #dc2626' },
  deadBadge: { background: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: '99px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const },
  deadBanner: { marginTop: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600 },
  hint: { textAlign: 'center' as const, fontSize: 12, color: '#94a3b8', padding: '12px', margin: 0 },
  trialBtn: { padding: '4px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #93c5fd', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deleteBtn: { padding: '4px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: 32, maxWidth: 480, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalTitle: { fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 12px' },
  modalText: { fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: '0 0 20px' },
  modalButtons: { display: 'flex', gap: 12, justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  confirmDeleteBtn: { padding: '10px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
  select: { width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', boxSizing: 'border-box' as const },
};
